import { normalizeNumberInput } from "@/lib/parse-number";
import {
  absBig,
  BIG_ONE,
  BIG_TWO,
  BIG_ZERO,
  formatBig,
  gcdBig,
  pow10Big,
} from "@/lib/tool-kit/math/bigint";

/**
 * Exact fractions on BigInt.
 *
 * The equation and coordinate tools read decimals and fractions as typed and keep them
 * exact, so 0.1 + 0.2 is 3/10 and a slope of 1/3 is never 0.333333333. A rational is
 * always stored reduced, with a positive denominator, so equality is field equality.
 */
export interface Rational {
  num: bigint;
  den: bigint;
}

/** Mantissa and exponent of an e-notation number: `6.02e23`, `1.5E-3`. */
const E_NOTATION = /^([^eE]+)[eE]([+-]?\d+)$/;
/** A typed fraction: `3/4`, `-1/2`. Each side is read as a decimal. */
const FRACTION = /^([^/]+)\/([^/]+)$/;
const LEADING_SIGN = /^[+-]/;

const MAX_EXPONENT = 30;

export function makeRational(num: bigint, den: bigint = BIG_ONE): Rational {
  if (den === BIG_ZERO) {
    throw new RangeError("zero denominator");
  }
  const sign = den < BIG_ZERO ? -BIG_ONE : BIG_ONE;
  const divisor = gcdBig(num, den);
  if (divisor === BIG_ZERO) {
    return { num: BIG_ZERO, den: BIG_ONE };
  }
  return { num: (sign * num) / divisor, den: (sign * den) / divisor };
}

export const RATIONAL_ZERO: Rational = { num: BIG_ZERO, den: BIG_ONE };

export function fromBigInt(n: bigint): Rational {
  return { num: n, den: BIG_ONE };
}

/** A machine decimal string ("-12.50", ".5", "3.") as an exact rational. */
function fromDecimalString(normalized: string): Rational {
  const negative = normalized.startsWith("-");
  const unsigned = normalized.replace(LEADING_SIGN, "");
  const point = unsigned.indexOf(".");
  const digits = unsigned.replace(".", "") || "0";
  const decimals = point === -1 ? 0 : unsigned.length - point - 1;
  const magnitude = BigInt(digits);
  return makeRational(negative ? -magnitude : magnitude, pow10Big(decimals));
}

/** A decimal as typed (decimal comma allowed, e-notation allowed), or null. */
function parseDecimalRational(raw: string): Rational | null {
  const text = raw.trim().replace("−", "-");
  const eMatch = E_NOTATION.exec(text);
  if (eMatch) {
    const mantissa = normalizeNumberInput(eMatch[1]);
    const exponent = Number.parseInt(eMatch[2], 10);
    if (mantissa === null || Math.abs(exponent) > MAX_EXPONENT) {
      return null;
    }
    const base = fromDecimalString(mantissa);
    const shift = pow10Big(Math.abs(exponent));
    return exponent >= 0
      ? makeRational(base.num * shift, base.den)
      : makeRational(base.num, base.den * shift);
  }
  const normalized = normalizeNumberInput(text);
  return normalized === null ? null : fromDecimalString(normalized);
}

/**
 * Read a typed number exactly: an integer, a decimal (`2.5`, `2,5`), e-notation
 * (`1.2e-3`) or a fraction (`3/4`, `-7/2`). Null when it is none of these, or when a
 * fraction has a zero denominator.
 */
export function parseRational(raw: string): Rational | null {
  const text = raw.trim();
  if (text === "") {
    return null;
  }
  const fraction = FRACTION.exec(text);
  if (fraction) {
    const top = parseDecimalRational(fraction[1]);
    const bottom = parseDecimalRational(fraction[2]);
    if (top === null || bottom === null || bottom.num === BIG_ZERO) {
      return null;
    }
    return divR(top, bottom);
  }
  return parseDecimalRational(text);
}

export function addR(a: Rational, b: Rational): Rational {
  return makeRational(a.num * b.den + b.num * a.den, a.den * b.den);
}

export function subR(a: Rational, b: Rational): Rational {
  return makeRational(a.num * b.den - b.num * a.den, a.den * b.den);
}

export function mulR(a: Rational, b: Rational): Rational {
  return makeRational(a.num * b.num, a.den * b.den);
}

export function divR(a: Rational, b: Rational): Rational {
  return makeRational(a.num * b.den, a.den * b.num);
}

export function negR(a: Rational): Rational {
  return { num: -a.num, den: a.den };
}

export function absR(a: Rational): Rational {
  return { num: absBig(a.num), den: a.den };
}

export function isZeroR(a: Rational): boolean {
  return a.num === BIG_ZERO;
}

export function isIntegerR(a: Rational): boolean {
  return a.den === BIG_ONE;
}

export function signR(a: Rational): number {
  if (a.num === BIG_ZERO) {
    return 0;
  }
  return a.num < BIG_ZERO ? -1 : 1;
}

export function equalsR(a: Rational, b: Rational): boolean {
  return a.num === b.num && a.den === b.den;
}

export function compareR(a: Rational, b: Rational): number {
  return signR(subR(a, b));
}

export function intR(n: number): Rational {
  return fromBigInt(BigInt(n));
}

/**
 * The value as a float, for decimals and graphs.
 *
 * Scaled before dividing so a fraction whose parts exceed 2^53 still converts to the
 * nearest double rather than to Infinity/Infinity.
 */
export function toNumberR(a: Rational): number {
  const whole = a.num / a.den;
  const rest = a.num - whole * a.den;
  const scale = pow10Big(17);
  const fraction = Number((rest * scale) / a.den) / 1e17;
  return Number(whole) + fraction;
}

/** Is the denominator of the form 2^i·5^j, so the decimal terminates? */
function terminatingDecimals(den: bigint): number | null {
  let rest = den;
  let twos = 0;
  let fives = 0;
  const five = BigInt(5);
  while (rest % BIG_TWO === BIG_ZERO) {
    rest /= BIG_TWO;
    twos += 1;
  }
  while (rest % five === BIG_ZERO) {
    rest /= five;
    fives += 1;
  }
  return rest === BIG_ONE ? Math.max(twos, fives) : null;
}

/**
 * The fraction as `p/q` with a real minus sign: `−3/4`, `5`.
 */
export function formatFraction(a: Rational): string {
  if (a.den === BIG_ONE) {
    return formatBig(a.num);
  }
  return `${formatBig(a.num)}/${a.den.toString()}`;
}

/**
 * The value the way a person would write it: an exact decimal when it terminates within
 * `maxDecimals` places (`0.125`, `−2.5`), otherwise the fraction (`1/3`).
 *
 * `decimalMark` lets a widget write `2,5` for a reader whose language uses a comma.
 */
export function formatRationalNice(
  a: Rational,
  decimalMark = ".",
  maxDecimals = 8
): string {
  if (a.den === BIG_ONE) {
    return formatBig(a.num);
  }
  const places = terminatingDecimals(a.den);
  if (places === null || places > maxDecimals) {
    return formatFraction(a);
  }
  const scaled = absBig(a.num) * (pow10Big(places) / a.den);
  const digits = scaled.toString().padStart(places + 1, "0");
  const integerPart = digits.slice(0, digits.length - places);
  const fractionPart = digits.slice(digits.length - places);
  const sign = a.num < BIG_ZERO ? "−" : "";
  return `${sign}${integerPart}${decimalMark}${fractionPart}`;
}

/** True when `formatRationalNice` would write a fraction rather than a decimal. */
export function needsFraction(a: Rational, maxDecimals = 8): boolean {
  if (a.den === BIG_ONE) {
    return false;
  }
  const places = terminatingDecimals(a.den);
  return places === null || places > maxDecimals;
}

/** Least common multiple of the denominators — what clears every fraction at once. */
export function commonDenominator(values: readonly Rational[]): bigint {
  let result = BIG_ONE;
  for (const value of values) {
    result = (result / gcdBig(result, value.den)) * value.den;
  }
  return result;
}

/**
 * How exact values are written: "fraction" (4/3, 5/2) or "decimal" (1.5, 2.5 — still a
 * fraction when the decimal would not terminate, as with 1/3).
 */
export type NumberStyle = "fraction" | "decimal";

export function formatStyled(
  value: Rational,
  style: NumberStyle,
  decimalMark = "."
): string {
  return style === "decimal"
    ? formatRationalNice(value, decimalMark)
    : formatFraction(value);
}

/** Does the styled text contain a slash, so it needs brackets before a variable? */
export function writesAsFraction(value: Rational, style: NumberStyle): boolean {
  return (
    value.den !== BigInt(1) && (style === "fraction" || needsFraction(value))
  );
}

export interface PolynomialTerm {
  coefficient: Rational;
  /** "x²", "x" or "" for the constant. */
  variable: string;
}

/** One term's magnitude, ready to sit before its variable: "", "3", "(3/2)". */
function termBody(
  magnitude: Rational,
  variable: string,
  style: NumberStyle,
  decimalMark: string
): string {
  const text = formatStyled(magnitude, style, decimalMark);
  if (variable === "") {
    return text;
  }
  if (equalsR(magnitude, intR(1))) {
    return "";
  }
  return writesAsFraction(magnitude, style) ? `(${text})` : text;
}

/** "2x² − 3x − 5", "x² − (3/2)x − 5/2", "0" — zero terms dropped. */
export function formatPolynomial(
  terms: readonly PolynomialTerm[],
  style: NumberStyle = "fraction",
  decimalMark = "."
): string {
  const parts: string[] = [];
  for (const { coefficient, variable } of terms) {
    if (isZeroR(coefficient)) {
      continue;
    }
    const negative = signR(coefficient) < 0;
    const body = termBody(
      negative ? negR(coefficient) : coefficient,
      variable,
      style,
      decimalMark
    );
    if (parts.length === 0) {
      parts.push(`${negative ? "−" : ""}${body}${variable}`);
    } else {
      parts.push(` ${negative ? "−" : "+"} ${body}${variable}`);
    }
  }
  return parts.length === 0 ? "0" : parts.join("");
}
